import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { get, post, del } from '../../api/client';
import './AdminDashboard.css';

interface StatsData {
    totalUsers: number;
    totalRepos: number;
    totalGroups: number;
}

interface UserItem {
    id: string;
    username: string;
    email: string;
    role?: string;
    isActive?: boolean;
    createdAt: string;
}

interface RepoItem {
    id: string;
    name: string;
    path: string;
    isPrivate: boolean;
    createdAt: string;
}

export function AdminDashboardPage() {
    const [stats, setStats] = useState<StatsData | null>(null);
    const [users, setUsers] = useState<UserItem[]>([]);
    const [repos, setRepos] = useState<RepoItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'repos'>('overview');

    // 创建用户表单状态
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [creating, setCreating] = useState(false);

    // 新创建用户的密钥显示
    const [createdUserSecret, setCreatedUserSecret] = useState<{ username: string; secret: string } | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [statsData, usersData, reposData] = await Promise.all([
                get<StatsData>('/admin/stats'),
                get<{ items: UserItem[] }>('/admin/users'),
                get<{ items: RepoItem[] }>('/admin/repos'),
            ]);
            setStats(statsData);
            setUsers(usersData.items);
            setRepos(reposData.items);
        } catch (err: any) {
            setError(err.message || '加载失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreating(true);
        setError(null);
        try {
            const result = await post<{ username: string; totpSecret: string }>('/admin/users', {
                username: newUsername,
                email: newEmail,
                password: newPassword,
            });
            // 保存新用户的密钥用于显示
            setCreatedUserSecret({
                username: result.username,
                secret: result.totpSecret,
            });
            setShowCreateUser(false);
            setNewUsername('');
            setNewEmail('');
            setNewPassword('');
            loadData();
        } catch (err: any) {
            setError(err.message || '创建用户失败');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteUser = async (userId: string, username: string) => {
        if (!window.confirm(`确定要删除用户 "${username}" 吗？`)) return;
        try {
            await del(`/admin/users/${userId}`);
            loadData();
        } catch (err: any) {
            setError(err.message || '删除用户失败');
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('zh-CN');
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="loading">加载中...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-dashboard">
                <div className="error-state">
                    <h2>访问被拒绝</h2>
                    <p>{error}</p>
                    <Link to="/" className="btn btn-primary">返回首页</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="page-header">
                <h1>管理后台</h1>
            </div>

            {/* 新用户密钥显示弹窗 */}
            {createdUserSecret && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        padding: 'var(--spacing-xl)',
                        borderRadius: 'var(--radius-lg)',
                        maxWidth: '500px',
                        width: '90%',
                        textAlign: 'center',
                    }}>
                        <h3 style={{ marginBottom: 'var(--spacing-md)' }}>✅ 用户创建成功</h3>
                        <p style={{ marginBottom: 'var(--spacing-sm)' }}>
                            用户 <strong>{createdUserSecret.username}</strong> 已创建
                        </p>
                        <p style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-warning)' }}>
                            ⚠️ 请立即保存以下 TOTP 密钥，此信息仅显示一次！
                        </p>
                        <div style={{
                            padding: 'var(--spacing-md)',
                            backgroundColor: 'var(--color-bg-tertiary)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--spacing-lg)',
                        }}>
                            <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
                                TOTP 密钥：
                            </p>
                            <code style={{
                                fontFamily: 'monospace',
                                fontSize: '18px',
                                letterSpacing: '3px',
                                wordBreak: 'break-all',
                                userSelect: 'all',
                            }}>
                                {createdUserSecret.secret}
                            </code>
                        </div>
                        <button
                            className="btn btn-primary"
                            onClick={() => setCreatedUserSecret(null)}
                            style={{ width: '100%' }}
                        >
                            我已保存，关闭
                        </button>
                    </div>
                </div>
            )}

            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    概览
                </button>
                <button
                    className={`tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    用户管理
                </button>
                <button
                    className={`tab ${activeTab === 'repos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('repos')}
                >
                    仓库管理
                </button>
            </div>

            {activeTab === 'overview' && stats && (
                <div className="overview-tab">
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon users">
                                <svg viewBox="0 0 16 16" width="24" height="24">
                                    <path fill="currentColor" d="M2 5.5a3.5 3.5 0 1 1 5.898 2.549 5.508 5.508 0 0 1 3.034 4.084.75.75 0 1 1-1.482.235 4 4 0 0 0-7.9 0 .75.75 0 0 1-1.482-.236A5.507 5.507 0 0 1 3.102 8.05 3.493 3.493 0 0 1 2 5.5Zm11 .5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm2.5-1a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm.615 6.707a1 1 0 0 1-.253 1.31A1 1 0 0 1 14 12.4a3.5 3.5 0 0 0-3.5-3.5.75.75 0 0 1 0-1.5 5 5 0 0 1 5 5h-.085a.75.75 0 0 1-.665.41.748.748 0 0 1-.635-.603Z" />
                                </svg>
                            </div>
                            <div className="stat-info">
                                <span className="stat-value">{stats.totalUsers}</span>
                                <span className="stat-label">用户</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon repos">
                                <svg viewBox="0 0 16 16" width="24" height="24">
                                    <path fill="currentColor" d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8Z" />
                                </svg>
                            </div>
                            <div className="stat-info">
                                <span className="stat-value">{stats.totalRepos}</span>
                                <span className="stat-label">仓库</span>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon groups">
                                <svg viewBox="0 0 16 16" width="24" height="24">
                                    <path fill="currentColor" d="M1.5 14.25c0 .138.112.25.25.25H4v-1.25a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 .75.75v1.25h2.25a.25.25 0 0 0 .25-.25V1.75a.25.25 0 0 0-.25-.25h-8.5a.25.25 0 0 0-.25.25ZM1.75 0A1.75 1.75 0 0 0 0 1.75v12.5c0 .966.784 1.75 1.75 1.75h8.5A1.75 1.75 0 0 0 12 14.25V1.75A1.75 1.75 0 0 0 10.25 0ZM3.75 4a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5ZM3 7.75A.75.75 0 0 1 3.75 7h4.5a.75.75 0 0 1 0 1.5h-4.5A.75.75 0 0 1 3 7.75Z" />
                                </svg>
                            </div>
                            <div className="stat-info">
                                <span className="stat-value">{stats.totalGroups}</span>
                                <span className="stat-label">群组</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'users' && (
                <div className="users-tab">
                    <div className="toolbar" style={{ marginBottom: 'var(--spacing-md)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowCreateUser(!showCreateUser)}
                        >
                            {showCreateUser ? '取消' : '+ 创建用户'}
                        </button>
                    </div>

                    {showCreateUser && (
                        <div className="create-form" style={{
                            background: 'var(--color-bg-secondary)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--spacing-lg)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            <h3 style={{ marginBottom: 'var(--spacing-md)' }}>创建新用户</h3>
                            <form onSubmit={handleCreateUser}>
                                <div className="form-group" style={{ marginBottom: 'var(--spacing-md)' }}>
                                    <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)' }}>用户名</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={newUsername}
                                        onChange={(e) => setNewUsername(e.target.value)}
                                        required
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: 'var(--spacing-md)' }}>
                                    <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)' }}>邮箱</label>
                                    <input
                                        type="email"
                                        className="form-input"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        required
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: 'var(--spacing-md)' }}>
                                    <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)' }}>密码 (至少8位)</label>
                                    <input
                                        type="password"
                                        className="form-input"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                        minLength={8}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <button type="submit" className="btn btn-primary" disabled={creating}>
                                    {creating ? '创建中...' : '创建用户'}
                                </button>
                            </form>
                        </div>
                    )}

                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>用户名</th>
                                    <th>邮箱</th>
                                    <th>角色</th>
                                    <th>状态</th>
                                    <th>注册时间</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td className="username">{user.username}</td>
                                        <td className="email">{user.email}</td>
                                        <td>
                                            <span className={`role-badge ${user.role}`}>{user.role === 'admin' ? '管理员' : '普通用户'}</span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${user.isActive !== false ? 'active' : 'inactive'}`}>
                                                {user.isActive !== false ? '活跃' : '禁用'}
                                            </span>
                                        </td>
                                        <td>{formatDate(user.createdAt)}</td>
                                        <td>
                                            {user.role !== 'admin' && (
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDeleteUser(user.id, user.username)}
                                                >
                                                    删除
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'repos' && (
                <div className="repos-tab">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>仓库</th>
                                    <th>路径</th>
                                    <th>可见性</th>
                                    <th>创建时间</th>
                                </tr>
                            </thead>
                            <tbody>
                                {repos.map((repo) => (
                                    <tr key={repo.id}>
                                        <td className="repo-name">{repo.name}</td>
                                        <td className="repo-path">
                                            <Link to={`/${repo.path}`}>{repo.path}</Link>
                                        </td>
                                        <td>
                                            <span className={`visibility-badge ${repo.isPrivate ? 'private' : 'public'}`}>
                                                {repo.isPrivate ? '私有' : '公开'}
                                            </span>
                                        </td>
                                        <td>{formatDate(repo.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboardPage;
